import { historicalGroups } from './historicalConfig.js';
import { translations, getLangFromURL } from '../map/i18n.js';

export class TimelineController {
    constructor(pano) {
        this.pano = pano;
        this.activeGroup = null;
        this.lang = getLangFromURL('el');
        this.t = translations[this.lang];
    }

    init() {
        this.injectHTML();
        
        if (this.pano && typeof this.pano.on === 'function') {
            this.pano.on('changenode', () => this.handleNodeChange());
            // Also run once on startup
            setTimeout(() => this.handleNodeChange(), 500);
        }
    }

    injectHTML() {
        if (document.getElementById('uoc-timeline-widget')) return;

        const widgetHTML = `
        <div class="uoc-timeline-widget hidden" id="uoc-timeline-widget">
            <div class="timeline-header">
                <i class="ph ph-clock-counter-clockwise"></i>
                <span id="timeline-title">${this.t.timeline.title}</span>
            </div>
            <div class="timeline-options" id="timeline-options">
                <!-- Dynamically populated buttons go here -->
            </div>
        </div>`;
        document.body.insertAdjacentHTML('beforeend', widgetHTML);
    }

    handleNodeChange() {
        if (!this.pano) return;
        const currentNode = this.pano.getCurrentNode();
        
        // Check if current node is part of any historical group
        this.activeGroup = historicalGroups.find(group => group.includes(currentNode));

        const widget = document.getElementById('uoc-timeline-widget');
        const optionsContainer = document.getElementById('timeline-options');

        if (!this.activeGroup || !widget || !optionsContainer) {
            if (widget) widget.classList.add('hidden');
            return;
        }

        // Show the widget
        widget.classList.remove('hidden');
        optionsContainer.innerHTML = '';

        // Populate options
        this.activeGroup.forEach(nodeId => {
            const userData = this.pano.getNodeUserdata(nodeId);
            if (!userData) return;

            // Extract capture date or fallback to title/id
            let dateStr = "Unknown Date";
            if (userData.datetime) {
                // e.g., "2021:05:28 14:00:00" -> extract just the date or year/month
                dateStr = userData.datetime.split(' ')[0].replace(/:/g, '-');
            } else if (userData.title) {
                dateStr = userData.title;
            } else {
                dateStr = nodeId;
            }

            const btn = document.createElement('button');
            btn.className = `timeline-btn ${nodeId === currentNode ? 'active' : ''}`;
            btn.textContent = dateStr;
            btn.title = `Switch to ${dateStr}`;
            
            btn.addEventListener('click', () => {
                if (nodeId !== currentNode) {
                    this.switchToNode(nodeId);
                }
            });

            optionsContainer.appendChild(btn);
        });
    }

    /**
     * A node's north offset, in degrees.
     *
     * Pano2VR stores this per node as userdata `heading`: the compass bearing you are facing
     * when pan is 0.
     *
     * Two things checked against the running player, because both looked like plausible
     * failure modes: it answers for nodes that have never been loaded, so a group does not
     * need to be visited first; and it answers even when pano.xml carries no heading attribute
     * for that node -- aerialher1 has none, and the player still reports 103.4 -- so the value
     * is derived, not read straight from the userdata tag. The 0 fallback below is therefore
     * only for a node id that does not resolve at all.
     */
    headingOf(nodeId) {
        const data = this.pano.getNodeUserdata(nodeId);
        const heading = data ? Number(data.heading) : NaN;

        return Number.isFinite(heading) ? heading : 0;
    }

    /**
     * Re-express a pan angle so it points the same way in the real world from another node.
     *
     * getPan() is relative to each panorama's own zero, not to north. Two aerial shots of the
     * same rooftop can be stitched with completely different offsets -- aerialher042026 sits at
     * -5.34 degrees and aerialher04 at +32.61 -- so carrying the raw pan across dropped the
     * viewer about 38 degrees away from what they had been looking at, which is exactly the
     * comparison the timeline exists to make.
     *
     * Pano2VR's convention is absolute = pan - heading, so matching bearings across a switch
     * means pan_target = pan_source - heading_source + heading_target. Verified against the
     * running tour: from aerialher042026 at pan 0 and at pan 120, the computed target pan lands
     * on the same coastline and the same mountain ridge respectively; the opposite sign lands
     * about 76 degrees away.
     *
     * Tilt and fov need no such correction: both are already absolute.
     */
    matchBearing(pan, sourceNodeId, targetNodeId) {
        let bearing = pan - this.headingOf(sourceNodeId) + this.headingOf(targetNodeId);

        // Pano2VR expects -180..180; the arithmetic above can leave it outside that.
        while (bearing > 180) bearing -= 360;
        while (bearing < -180) bearing += 360;

        return bearing;
    }

    switchToNode(targetNodeId) {
        if (!this.pano) return;

        const sourceNodeId = this.pano.getCurrentNode();

        // Extract current viewing parameters
        const pan = this.pano.getPan() || 0;
        const tilt = this.pano.getTilt() || 0;
        const fov = this.pano.getFov() || 70;

        const targetPan = this.matchBearing(pan, sourceNodeId, targetNodeId);

        /*
         * openNext takes the start view as an OBJECT. Passing the "pan,tilt,fov" string that
         * the URL-hash parser builds is accepted without complaint and then silently ignored --
         * the node opens on its own default view instead. That is why this feature appeared to
         * do nothing at all: every switch landed on the target's stored view, and no amount of
         * correcting the pan could show up.
         */
        this.pano.openNext(`{${targetNodeId}}`, {
            pan: targetPan,
            tilt: tilt,
            fov: fov,
        });
    }

    setLanguage(lang) {
        if (!translations[lang]) lang = 'el';
        this.lang = lang;
        this.t = translations[lang];
        
        const titleEl = document.getElementById('timeline-title');
        if (titleEl) {
            titleEl.textContent = this.t.timeline.title;
        }
    }
}